<template>
  <div class="slider-related-products">
    <div class="slider-related-products__head">
      <div class="--title h2">{{ title }}</div>
      <div class="--navigation">
        <div class="--prev" @click="prevSlide"><img src="~/assets/img/svg/common/arrow-down.svg" alt=""></div>
        <div class="--next" @click="nextSlide"><img src="~/assets/img/svg/common/arrow-down.svg" alt=""></div>
      </div>

      <template v-if="linkUrl">
        <nuxt-link :to="linkUrl" class="--link hover-decoration">
          {{ linkTitle }}
          <img src="~/assets/img/svg/common/arrow.svg" alt=""/>
        </nuxt-link>
      </template>
    </div>

    <template v-if="isTemplateLoading">
      <div class="slider-related-products__skeleton loading-skeleton"/>
    </template>
    <template v-if="!isTemplateLoading">
      <swiper
        class="slider-related-products__swiper"
        :modules="[Navigation, Virtual]"
        :slides-per-view="1.2"
        :space-between="8"
        :init="true"
        :loop="isSwiperLoop"
        :breakpoints="{
        1360: {slidesPerView: 4},
        860: {slidesPerView: 3},
        640: {slidesPerView: 2.1},
        520: {slidesPerView: 1.4},
      }"
        @swiper="setMainSwiper"
        @slideChange="changeActiveSlide"
      >
        <swiper-slide
          v-for="(product) in products"
          :key="`product-${ product.slug }`"
          class="swiper-slide"
        >
          <template v-if="type === 'collections' || product.isProductSet">
            <CollectionCard :product="product"/>
          </template>
          <template v-else>
            <ProductCard :product="product"/>
          </template>
        </swiper-slide>
      </swiper>
      <template v-if="linkUrl">
        <div class="slider-related-products__footer">
          <nuxt-link :to="linkUrl" class="btn btn-outline">
            {{ linkTitle }}
          </nuxt-link>
        </div>
      </template>
      <div ref="sectionProducts"/>
    </template>
  </div>
</template>

<script setup lang="ts">
import {Swiper, SwiperSlide} from 'swiper/vue';
import {Navigation, Virtual} from 'swiper/modules';
import { type IProduct, Product } from '#sitis/internal/controllers/products/models/Product';
import ProductCard from '~/components/catalog/ProductCard.vue';
import CollectionCard from '~/components/catalog/CollectionCard.vue';
import {ref} from "vue";
import type {Swiper as SwiperClass} from "swiper/types";
import {useIntersectionObserver} from "@vueuse/core";
import useEcommerce from "~/composables/useEcommerceYandex";


const { $ym, $viewport } = useNuxtApp();
const { addEcommerceProductsList } = useEcommerce();

interface IProps {
  type: string;
  title: string;
  linkUrl?: string;
  linkTitle?: string;
  products: IProduct[]
  goal?: string;
};
const props = withDefaults(defineProps<IProps>(), {
  type: "",
  title: "",
  products: () => []
});

const isSwiperLoop: ComputedRef<boolean> = computed(() => {
  if ($viewport.isGreaterOrEquals('lg')) {
    return props.products.length > 6
  }
  return props.products.length > 3
});

const mainSwiper: Ref<any> = ref(null);
const setMainSwiper = (_mainSwiper: any) => {
  mainSwiper.value = _mainSwiper;
};
const prevSlide = () => {
  if (!mainSwiper.value) {
    return
  }
  mainSwiper.value.slidePrev();
}
const nextSlide = () => {
  if (!mainSwiper.value) {
    return
  }
  mainSwiper.value.slideNext();
}

const activeSlide = ref(1);

const changeActiveSlide = (event: SwiperClass) => {
  const eventSwiper: any = event;
  const isDisabled = Boolean(eventSwiper?.isLocked || !eventSwiper?.initialized || eventSwiper?.realIndex === 0);
  if (isDisabled) {
    // фикс отправки яндекс метрики при разрушении свайперов
    return
  }

  activeSlide.value = (event?.realIndex || 0) + 1;
  const canUseYm = import.meta.client && typeof $ym === 'function' && typeof props.goal === 'string' && props.goal.length > 0;
  if (canUseYm) {
    $ym('reachGoal', props.goal);
  }
};

const isSendingProducts: Ref<boolean> = ref(false);
const sectionProducts = ref<HTMLDivElement | null>();
const sendingProductsTo = async () => {
  isSendingProducts.value = true;
  addEcommerceProductsList(props.products, `${props.title}`);
}
const { stop } = useIntersectionObserver(
  sectionProducts,
  async ([{ isIntersecting }]) => {
    if (isIntersecting && !isSendingProducts.value) {
      await sendingProductsTo();
    }
  },
  {

  }
);

const isTemplateLoading: Ref<boolean> = ref(true);
onMounted(() => {
  setTimeout(() => {
    isTemplateLoading.value = false;
  }, (2 * 1000))
});
</script>

<style scoped lang="scss">
.slider-related-products {
  position: relative;
}
.slider-related-products__head {
  display: flex;
  margin-bottom: 32px;

  .--title {
    color: black;
  }
  .--navigation {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 32px;

    & > * {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px; height: 32px;
      cursor: pointer;
      border-radius: 7px;
      background-color: #EBEBEB;

      &:hover {
        background-color: #E0DEDE;
      }
      &:active {
        background-color: #C3C3C3;
      }
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    .--prev img {
      transform: rotate(90deg);
    }
    .--next img {
      transform: rotate(-90deg);
      margin-bottom: -2px;
    }
  }
  .--link {
    display: flex;
    align-items: center;
    margin-left: auto;

    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    font-feature-settings: 'pnum' on, 'lnum' on;
    color: #000000;

    img {
      width: 18px;
      height: 18px;
      margin-left: 6px;
      object-fit: contain;
      object-position: center;
      transform: rotate(180deg);
    }
  }
}
.slider-related-products__swiper {
  padding-bottom: 85px;
  margin-bottom: -85px;

  .swiper-slide {
    height: auto;
    display: flex;
    z-index: initial;
    flex-direction: column;
    &:hover {
      z-index: 5;
    }
  }
  .swiper-slide > * {
    height: 100%;
  }
}
.slider-related-products__footer {
  margin-top: 32px;
  display: none;
}
.slider-related-products__skeleton {
  height: 448px;
  position: relative;
  overflow: hidden;
  border-radius: 20px;
}

@media (min-width: 1360px) {
  .slider-related-products__swiper {
    margin-left: -80px;
    padding-left: 80px;
    margin-right: -80px;
    padding-right: 80px;
  }
}
@media (min-width: 1200px) and (max-width: 1359px){
  .slider-related-products__swiper {
    margin-left: -40px;
    padding-left: 40px;
    margin-right: -40px;
    padding-right: 40px;
  }
}
@media (max-width: 1023px) {
  .slider-related-products__head {
    margin-bottom: 16px;
    .--link {
      display: none;
    }
    .--navigation {
      margin-left: auto;
    }
  }
  .slider-related-products__swiper {
    padding-bottom: 0;
    margin-bottom: 0;
  }
  .slider-related-products__footer {
    display: block;
  }
}
@media (max-width: 639px) {
  .slider-related-products__swiper {
    margin-left: -20px;
    margin-right: -20px;
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
